import React, { ReactElement, useMemo, useState } from "react";
import { Delegation, DelegationsList } from "../../models/Validator";
import {
  MantineReactTable,
  MRT_ColumnDef,
  MRT_SortingState,
  useMantineReactTable,
} from "mantine-react-table";
import CopyTextComponent from "../copyText/CopyText.component";
import {
  convertFromGwei,
  formatAddress,
  formatAmountWithLabel,
} from "../../utils/formatter";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { QueryKey } from "../../models/enums";
import { IndexerService } from "../../services/indexer.service";
import { formatDate } from "../../utils/formatTime";
import styles from "../addressDetailsTabs/AddressDetailsTabs.module.scss";
import { useStore } from "@tanstack/react-store";
import { IStore, store } from "../../services/store.service";

const initialPagination = {
  pageIndex: 0,
  pageSize: 10, //customize the default page size
};
export default function CurrentDelegationsTable({
  nodeId,
}: {
  nodeId: string;
}): ReactElement {
  const timeZone = useStore<IStore, IStore["timeZone"]>(store, (state) => state.timeZone).selected;

  //store pagination state in your own state
  const [pagination, setPagination] = useState<{
    pageIndex: number;
    pageSize: number;
  }>(initialPagination);
  const [sorting, setSorting] = useState<MRT_SortingState>([]);

  const { data: delegationsList } = useQuery({
    queryKey: [
      QueryKey.delegationsList,
      nodeId,
      pagination.pageIndex,
      pagination.pageSize,
      sorting,
    ],
    queryFn: (): Promise<DelegationsList> => {
      const timestampSorting = sorting.find((item) => item.id === "endTime");
      const amountSortDirection = sorting.find((item) => item.id === "amount");

      return IndexerService.getValidatorDelegations(
        nodeId!,
        pagination.pageSize,
        pagination.pageIndex * pagination.pageSize,
        amountSortDirection
          ? amountSortDirection.desc
            ? "desc"
            : "asc"
          : undefined,
        timestampSorting ? (timestampSorting.desc ? "desc" : "asc") : undefined
      );
    },

    enabled: !!nodeId,
    placeholderData: keepPreviousData,
  });

  //should be memoized or stable
  const columns = useMemo<MRT_ColumnDef<Delegation>[]>(
    () => [
      {
        accessorKey: "txID",
        header: "Transaction ID",
        mantineTableHeadCellProps: {
          align: "left",
        },
        mantineTableBodyCellProps: {
          align: "left",
        },
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ cell }) => (
          <CopyTextComponent
            primaryColor={false}
            text={formatAddress(cell.getValue<string>())}
            value={cell.getValue<string>()}
            link={`/tx/${cell.getValue<string>()}`}
            size={"14px"}
            weight={"500"}
          ></CopyTextComponent>
        ),
      },
      {
        accessorKey: "address",
        header: "Address",
        mantineTableHeadCellProps: {
          align: "left",
        },
        mantineTableBodyCellProps: {
          align: "left",
        },
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ cell }) => (
          <CopyTextComponent
            primaryColor={false}
            text={formatAddress(
              cell.getValue<string>(),
              undefined,
              undefined,
              true
            )}
            value={cell.getValue<string>()}
            link={`/address/${cell.getValue<string>()}`}
            size={"14px"}
            weight={"500"}
          ></CopyTextComponent>
        ),
      },
      {
        accessorKey: "amount",
        header: "Amount",
        mantineTableHeadCellProps: {
          align: "right",
        },
        mantineTableBodyCellProps: {
          align: "right",
        },
        enableSorting: true,
        enableColumnFilter: false,
        Cell: ({ cell }) => (
          <>
            {formatAmountWithLabel(
              Math.floor(convertFromGwei(cell.getValue<number>()) * 100) / 100, //We want to have always 2 digits in this column, without rounding.
              false,
              2
            )}
          </>
        ),
      },
      {
        accessorKey: "endTime",
        header: "End time",
        size: 220,
        mantineTableHeadCellProps: {
          align: "left",
          fontSize: "14px",
        },

        mantineTableBodyCellProps: {
          align: "left",
        },
        enableSorting: true,
        enableColumnFilter: false,
        Cell: ({ cell }) => (
          <>
            {cell.getValue<string>() === null ? (
              <span> No date provided</span>
            ) : (
              <span>
                {
                  formatDate(new Date(cell.getValue<string>()), timeZone)
                    .formattedDateTime
                }
                &nbsp;
                <span className={styles.unitLabel}>
                  {formatDate(new Date(cell.getValue<string>()), timeZone).utcOffset}
                </span>
              </span>
            )}
          </>
        ),
      },
    ],
    [delegationsList?.delegations, timeZone]
  );

  const table = useMantineReactTable({
    columns: delegationsList?.delegations ? columns : [],
    data: delegationsList?.delegations || [], //must be memoized or stable (useState, useMemo, defined outside of this component, etc.)
    mantineTableProps: {
      style: {
        tableLayout: "fixed",
        fontWeight: 500,
        fontSize: "var(--module-size-14)",
      },
    },
    mantineTableHeadCellProps: {
      style: {
        fontWeight: 500,
        fontSize: 15,
      },
    },
    state: {
      isLoading: !delegationsList,
      pagination,
      sorting,
    },
    mantinePaperProps: {
      withBorder: false,
      shadow: "none",
    },
    enableColumnActions: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableFilters: false,
    initialState: {
      density: "xs",
    },
    manualPagination: true,
    manualSorting: true,
    rowCount: delegationsList?.count,
    onPaginationChange: setPagination, //hoist pagination state to your state when it changes internally
    onSortingChange: setSorting,
    mantinePaginationProps: {
      showRowsPerPage: false,
      withEdges: false,
    },
  });

  return (
    <div
      style={{
        height: delegationsList && delegationsList.count >= 10 ? 515 : "unset",
      }}
    >
      <MantineReactTable table={table} />
    </div>
  );
}
